import React from 'react'
import { RouteComponentProps, NavLink, useRouteMatch } from 'react-router-dom'
import { useInView } from 'react-intersection-observer'
import {
  Transition,
  Segment,
  BannersDisplay,
  BannerPageWrapper,
  Flex,
  DocumentHeader,
  useBreakpoint,
} from '@sketch/components'
import {
  RouteParams,
  routes,
  useGetNewNotificationsCount,
} from '@sketch/modules-common'
import { useEventDispatch } from '@sketch/utils'

import NotificationsListByType from '../../../activity/components/Notifications/NotificationsListByType'
import { WorkspaceSidebarLayoutExtraProps } from '../../../workspace/components/WorkspaceSidebarLayout/WorkspaceSidebarLayout'
import {
  useNotificationMarkAllAsReadMutation,
  useGetNotificationsQuery,
  GetUserNotificationsCountDocument,
} from '@sketch/gql-types'
import {
  notificationMarkAllAsReadOptimisticResponse,
  notificationsQueryOptions,
} from '@sketch/user'
import {
  MarkAllAsReadButton,
  StyledMarkAsReadIcon,
  StyledSkeleton,
  SearchWrapper,
  NavbarSection,
  NavbarAnimation,
  NavbarAnimationWrapper,
} from './Updates.styles'
import { NotificationsPill } from '../../components/UserNavbarSection/UserDropdown/UserDropdown'
import { NotificationSearch } from '../../../activity/components/Notifications/NotificationSearch/NotificationSearch'
/**
 * TYPES
 */
export type UpdatesRoutesProps = RouteComponentProps<RouteParams<'UPDATES'>>

export type UpdatesViewProps = UpdatesRoutesProps &
  WorkspaceSidebarLayoutExtraProps

declare module '@sketch/utils' {
  export interface EventsMap {
    userMarkedAllNotificationsAsRead: void
  }
}

/**
 * MAIN COMPONENT
 */
export const Updates: React.FCC<UpdatesViewProps> = ({
  HeaderPortal,
  NavbarPortal,
  HeaderSlimPortal,
  useOverrideLayoutProps,
}) => {
  const isTabletOrBigger = useBreakpoint('sm')
  const { ref, inView: headerNavbarInView } = useInView()
  const navbarAnimationRef = React.useRef<HTMLDivElement>(null)

  const { data, loading } = useGetNotificationsQuery(notificationsQueryOptions)

  const { path } = useRouteMatch()

  /**
   * We are using a event emitter here, instead of refetching the query when the mutation is successful
   * Given we have options and search fields, we would need to mimic the filter logic, creating a dependency between
   * the mutation and the query. Using the event emitter we reduce this dependency we just launch the event and if
   * someone is listening, it self will be in charge of updating
   */
  const userMarkedAsRead = useEventDispatch('userMarkedAllNotificationsAsRead')

  const [markAllNotificationsAsRead] = useNotificationMarkAllAsReadMutation({
    redirectErrors: true,
    onError: 'do-nothing',
    optimisticResponse: {
      __typename: 'RootMutationType',
      ...notificationMarkAllAsReadOptimisticResponse,
    },
    UNSAFE_ignoreResults: true,
    refetchQueries: [{ query: GetUserNotificationsCountDocument }],
    onCompleted: () => {
      userMarkedAsRead()
    },
    awaitRefetchQueries: true,
  })

  const unreadNotifications = useGetNewNotificationsCount({
    fetchPolicy: 'cache-only',
  })

  const hasNotifications = data?.me.notifications?.entries.length !== 0
  const hasUnreadNotifications = data?.me?.hasUnreadNotifications || false

  useOverrideLayoutProps({
    title: 'Updates',
    hasNavbar: false,
  })

  const mobileNavbar = (
    <>
      <Segment
        type="text"
        label="Unread"
        render={(className, children) => (
          <NavLink exact className={className} to={routes.UPDATES.create({})}>
            <Flex alignItems="center">
              {children}
              {hasUnreadNotifications && (
                <Flex pr={2} alignItems="center">
                  <NotificationsPill
                    unreadNotifications={unreadNotifications}
                    withNumberDisplay
                  />
                </Flex>
              )}
            </Flex>
          </NavLink>
        )}
      />
      <Segment
        type="text"
        label="Read"
        disabled={!hasNotifications}
        render={(className, children) => (
          <NavLink
            exact
            className={className}
            to={routes.UPDATES_READ.create({})}
          >
            {children}
          </NavLink>
        )}
      />
    </>
  )

  const navbar = (
    <NavbarSection>
      {mobileNavbar}

      {path !== routes.UPDATES_READ.template() && (
        <Segment
          type="text"
          label="Mark all as read"
          render={() => {
            if (loading) {
              return <StyledSkeleton width="150px" height="32px" />
            }

            return (
              <MarkAllAsReadButton
                onClick={markAllNotificationsAsRead}
                disabled={!hasNotifications || !hasUnreadNotifications}
              >
                <StyledMarkAsReadIcon />
                Mark all as read
              </MarkAllAsReadButton>
            )
          }}
        />
      )}
    </NavbarSection>
  )

  return (
    <>
      <HeaderPortal>
        <DocumentHeader
          title="Updates"
          description="Keep track of all comments and notifications about documents you’re working on."
          actions={<>{navbar}</>}
        />
      </HeaderPortal>

      <HeaderSlimPortal>
        <DocumentHeader title="Updates" />
      </HeaderSlimPortal>

      {isTabletOrBigger && (
        <NavbarPortal>
          <SearchWrapper>
            <NotificationSearch />
          </SearchWrapper>
        </NavbarPortal>
      )}

      <Transition
        nodeRef={navbarAnimationRef}
        timeout={200}
        mountOnEnter
        unmountOnExit
        in={!isTabletOrBigger && !headerNavbarInView}
      >
        {state => (
          <NavbarPortal>
            <NavbarAnimationWrapper>
              <NavbarAnimation
                ref={navbarAnimationRef}
                data-animation-state={state}
              >
                <NavbarSection>{mobileNavbar}</NavbarSection>
              </NavbarAnimation>
            </NavbarAnimationWrapper>
          </NavbarPortal>
        )}
      </Transition>

      <BannerPageWrapper $hideMargins="top">
        <BannersDisplay />
      </BannerPageWrapper>

      {!isTabletOrBigger && (
        <>
          <NotificationSearch />
          <DocumentHeader
            title="Updates"
            description="Keep track of all comments and notifications about documents you’re working on."
            actions={<div ref={ref}>{navbar}</div>}
          />
        </>
      )}

      <NotificationsListByType
        notificationType={
          path === routes.UPDATES_READ.template() ? 'READ' : 'UNREAD'
        }
      />
    </>
  )
}
